import type { CogsMeasure } from "./api";
import { ENTITY_LABEL } from "./config";
import { signedPct } from "./format";

/** C1 v1.39 (c1-cogs-detail, gap sweep Price Run rows 59 and 61): the drawer's words for the two COGS details C4 v1.13 serves on
 * `cogs_measures`. The portal compares nothing: the direction is C4's percentage as served (H12). */
export const COGS_DETAIL_COPY = {
  directionOnly: "Direction only",
  notTheBasis: "Not the basis",
  units: (count: string, one: boolean) => `${count} ${one ? "unit" : "units"} in stock`,
  noneInStock: "none in stock",
  belowZero: (count: string) => `none in stock (Xero: ${count})`,
  above: (pct: string) => `${pct}% above the current inventory cost`,
  below: (pct: string) => `${pct}% below the current inventory cost`,
  same: "the same as the current inventory cost",
  title: (date: string) => `Last Production COGS of ${date} against Current Inventory COGS`,
};

const UNITS = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 4 });

/** The inventory line's units (v26 "412 units available, Ketosource Ltd"; owner ruling A3: "in stock", because the figure is
 * Xero's quantity on hand, not stock free to sell): C4's decimal string grouped as en-GB, "none in stock" at zero and
 * "none in stock (Xero: -3)" below it (string tests, never arithmetic), the entity by its Config name (the code itself for one
 * Config does not name). Null when the measure carries no units: an older grid, or an inventory cost whose Xero read named none. */
export function unitsText(measure: CogsMeasure | undefined): string | null {
  if (!measure || "missing" in measure || measure.quantity_available == null) return null;
  const count = measure.quantity_available;
  const units = count === "0" ? COGS_DETAIL_COPY.noneInStock
    : count.startsWith("-") ? COGS_DETAIL_COPY.belowZero(count)
    : COGS_DETAIL_COPY.units(UNITS.format(Number(count)), count === "1");
  return measure.entity ? `${units}, ${ENTITY_LABEL[measure.entity] ?? measure.entity}` : units;
}

export type DirectionTone = "up" | "down" | "flat";
export type Direction = { text: string; said: string; tone: DirectionTone };

/** The production line's direction chip (v26 `dirOf`): ↑ when the production cost is above the inventory average cost, ↓ when
 * below, → at 0.0. Null when no direction is served. */
export function directionOf(measure: CogsMeasure | undefined): Direction | null {
  if (!measure || "missing" in measure || measure.direction_pct == null) return null;
  const pct = measure.direction_pct;
  if (pct === "0.0" || pct === "-0.0") return { text: "→ 0.0%", said: COGS_DETAIL_COPY.same, tone: "flat" };
  if (pct.startsWith("-")) return { text: `↓ ${signedPct(pct)}`, said: COGS_DETAIL_COPY.below(pct.slice(1)), tone: "down" };
  return { text: `↑ ${signedPct(pct)}`, said: COGS_DETAIL_COPY.above(pct), tone: "up" };
}

export const DIRECTION_TONE: Record<DirectionTone, string> = {
  up: "border-ks-warn text-ks-warn",
  down: "border-ks-pass text-ks-pass",
  flat: "text-ks-grey-1",
};

/** The chip's tone (owner ruling A2): coloured only when production is not the basis, where the margins on screen will move as
 * the stock turns over (↑ amber, the margin falls; ↓ green); on the Production basis the margins already use that cost, so the
 * chip is grey whatever its arrow. */
export function directionClass(tone: DirectionTone, onBasis: boolean): string {
  return onBasis ? DIRECTION_TONE.flat : DIRECTION_TONE[tone];
}
